import React, { useState } from "react";
import { AnimatePresence, Variants, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { BuildingStorefrontIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { Outlets } from "@types";
import { joinClassNames } from "@utils/helpers";
import { useHydrated, useWindowSize } from "@utils/hooks";
import { useIsShopsite } from "@context/DomainContext";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import DialogCloseButton from "@components/popovers/dialogCloseButton";
import { Button, Card, Heading, Text } from "@components/theming";

interface Props {
  /**
   * takes in outlets as props that are available to buy tickets from
   */
  outlets: Outlets[];
}

const Outlet = ({ outlets }: Props) => {
  const windowSize = useWindowSize();
  const [open, setOpen] = useState(false);
  const hydrated = useHydrated();
  const isIOS = hydrated ? navigator.userAgent.includes("like Mac") : false;
  const t = useTranslations("EventPage");
  const isShopsite = useIsShopsite();

  const variants: Variants = {
    hidden: {
      y: windowSize.width && windowSize.width <= 1024 ? "100%" : 0,
      opacity: 0,
      scale: windowSize.width && windowSize.width <= 1024 ? 1 : 0.9,
      backdropFilter: isShopsite ? "" : "blur(64px) opacity(0)",
      transition: { duration: 0.125 },
    },
    show: {
      y: 0,
      opacity: 1,
      scale: 1,
      backdropFilter: isShopsite ? "" : "blur(64px) opacity(1)",
    },
  };

  return (
    <div
      className={joinClassNames(
        "flex flex-col gap-1",
        isShopsite ? "bg-gray-50 rounded-2xl px-4 py-2" : "mt-4"
      )}
    >
      <div className="flex gap-1 items-center">
        {isShopsite && (
          <BuildingStorefrontIcon
            className="h-5 w-5 text-black"
            aria-hidden="true"
          />
        )}
        <Heading
          as="h2"
          weight="bold"
          shopsiteClasses="text-base font-semibold text-black"
        >
          {t("outlet.heading")}
        </Heading>
      </div>
      <Text as="p" color="gray" shopsiteClasses="text-gray-500">
        {t("outlet.description")}
      </Text>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="soft"
            shopsiteVariant="soft"
            shopsiteClasses="py-1 !rounded-lg gap-1"
            whitelabelClasses="w-full"
          >
            <BuildingStorefrontIcon className="h-5 w-5" aria-hidden="true" />
            <Heading className="text-base font-semibold">
              {t("outlet.trigger")}
            </Heading>
          </Button>
        </DialogTrigger>
        <AnimatePresence>
          {open && (
            <DialogContent forceMount handleClose={() => setOpen(false)}>
              <Card asChild>
                <motion.div
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={variants}
                  transition={{ duration: 0.25 }}
                >
                  <div className="flex flex-col max-h-[95dvh]">
                    <div
                      className={joinClassNames(
                        "flex flex-col pb-2",
                        isShopsite ? "px-6 pt-6 pb-4 gap-4 bg-indigo-500" : ""
                      )}
                    >
                      <div className="relative self-end">
                        <DialogClose asChild>
                          <DialogCloseButton shopsiteVariant="white" />
                        </DialogClose>
                      </div>
                      <DialogHeader className="!text-left">
                        <DialogTitle asChild>
                          <Heading
                            as="h3"
                            size="5"
                            weight="bold"
                            shopsiteClasses="!text-white text-xl font-bold"
                          >
                            {t("outlet.dialogheading")}
                          </Heading>
                        </DialogTitle>
                        <BuildingStorefrontIcon
                          className={joinClassNames(
                            "absolute top-0 left-4 md:w-19 md:h-19 -rotate-12",
                            isShopsite
                              ? "text-white/10 w-24 h-24"
                              : "text-[color:var(--accent-a3)] w-16 h-16"
                          )}
                        />
                      </DialogHeader>
                    </div>
                    <ul
                      className={joinClassNames(
                        "flex flex-col items-start gap-4 overflow-auto",
                        isShopsite ? "px-6 py-4 text-gray-600" : ""
                      )}
                    >
                      {outlets.map((outlet) => (
                        <li
                          key={outlet.id}
                          className="flex flex-col md:flex-row items-center gap-y-4 md:gap-y-0 md:gap-x-4"
                        >
                          <Image
                            src={outlet.logo.trim()}
                            width={150}
                            height={100}
                            className={joinClassNames(
                              "object-cover",
                              isShopsite
                                ? "rounded-md"
                                : "rounded-[var(--radius-2)]"
                            )}
                            alt="outlet store"
                          />
                          <div className="flex flex-col gap-2">
                            <Heading
                              as="h3"
                              size="4"
                              shopsiteClasses="font-semibold text-lg"
                            >
                              {outlet.name}
                            </Heading>
                            <Button
                              variant="soft"
                              shopsiteVariant="soft"
                              whitelabelClasses="h-fit py-1 text-left"
                              shopsiteClasses="gap-1 py-1 text-sm leading-6 !font-medium rounded-lg"
                              sharedClasses="w-fit"
                              href={`${
                                isIOS ? "maps" : "https"
                              }://maps.google.com/maps/search/${outlet.name},${
                                outlet.address
                              },${outlet.region}`}
                            >
                              <MapPinIcon className="w-5 h-5" />
                              <span>
                                {outlet.address}, {outlet.region}
                              </span>
                            </Button>
                            <Text
                              as="p"
                              size="2"
                              color="gray"
                              shopsiteClasses="prose text-sm text-gray-500"
                              dangerouslySetInnerHTML={{ __html: outlet.hours }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </Card>
            </DialogContent>
          )}
        </AnimatePresence>
      </Dialog>
    </div>
  );
};

export default Outlet;
